import { createContext, useContext, useCallback, useEffect, useRef, useState } from "react";
import { DEFAULT_LANG, STORAGE_KEY, langMeta, LANGUAGES } from "@/i18n/languages";

// Lazy dictionary loaders — each locale JSON is code-split so English visitors
// never download translation payloads.
const loaders = {
  es: () => import("@/i18n/locales/es.json"),
  pt: () => import("@/i18n/locales/pt.json"),
  fr: () => import("@/i18n/locales/fr.json"),
  de: () => import("@/i18n/locales/de.json"),
  nl: () => import("@/i18n/locales/nl.json"),
  he: () => import("@/i18n/locales/he.json"),
  zh: () => import("@/i18n/locales/zh.json"),
};

const LanguageContext = createContext({ lang: DEFAULT_LANG, setLang: () => {} });
export const useLanguage = () => useContext(LanguageContext);

// Attributes whose values should be translated (user-facing text).
const ATTRS = ["placeholder", "title", "aria-label", "alt"];
// Skip these element types entirely.
const SKIP_TAGS = new Set(["SCRIPT", "STYLE", "NOSCRIPT", "IFRAME", "CODE", "PRE"]);

function splitWS(str) {
  const m = str.match(/^(\s*)([\s\S]*?)(\s*)$/);
  return { lead: m[1], core: m[2], trail: m[3] };
}

// Detect the visitor's preferred language from the browser and map it to a
// supported locale. Returns null if none match (falls back to English).
function detectBrowserLang() {
  if (typeof navigator === "undefined") return null;
  // Keep the react-snap prerender snapshot in English.
  if (navigator.userAgent === "ReactSnap") return null;
  const supported = new Set(LANGUAGES.map((l) => l.code));
  const prefs =
    navigator.languages && navigator.languages.length
      ? navigator.languages
      : [navigator.language];
  for (const pref of prefs) {
    if (!pref) continue;
    let primary = pref.toLowerCase().split("-")[0];
    if (primary === "iw") primary = "he"; // legacy Hebrew code
    if (primary === "in") primary = "id"; // legacy (unsupported, ignored)
    if (supported.has(primary)) return primary;
  }
  return null;
}

// Read an explicit ?lang= override from the URL. Lets hreflang alternate URLs
// (e.g. /properties?lang=es) load directly in that language.
function detectParamLang() {
  if (typeof window === "undefined") return null;
  const supported = new Set(LANGUAGES.map((l) => l.code));
  try {
    const p = new URLSearchParams(window.location.search).get("lang");
    if (p && supported.has(p)) return p;
  } catch (e) {
    /* ignore */
  }
  return null;
}

export function LanguageProvider({ children }) {
  const [lang, setLangState] = useState(DEFAULT_LANG);
  const [, setDictTick] = useState(0); // re-render t() consumers once a dictionary loads
  const dictRef = useRef({}); // current translation map (English core -> translated)
  const textOrig = useRef(new WeakMap()); // text node -> original English value
  const attrOrig = useRef(new WeakMap()); // element -> { attr: originalValue }
  const observerRef = useRef(null);
  const langRef = useRef(DEFAULT_LANG);

  const shouldSkip = (node) => {
    let el = node.nodeType === 3 ? node.parentElement : node;
    while (el) {
      if (SKIP_TAGS.has(el.tagName)) return true;
      if (el.hasAttribute && el.hasAttribute("data-no-translate")) return true;
      el = el.parentElement;
    }
    return false;
  };

  const translateTextNode = useCallback((node, dict) => {
    if (!node.nodeValue || !node.nodeValue.trim()) return;
    if (shouldSkip(node)) return;
    let orig = textOrig.current.get(node);
    if (orig === undefined) {
      orig = node.nodeValue;
      textOrig.current.set(node, orig);
    }
    const { lead, core, trail } = splitWS(orig);
    const translated = dict[core];
    const next = translated ? lead + translated + trail : orig;
    if (node.nodeValue !== next) node.nodeValue = next;
  }, []);

  const translateAttrs = useCallback((el, dict) => {
    if (!el.getAttribute || shouldSkip(el)) return;
    let store = attrOrig.current.get(el);
    ATTRS.forEach((attr) => {
      if (!el.hasAttribute(attr)) return;
      if (!store) {
        store = {};
        attrOrig.current.set(el, store);
      }
      if (store[attr] === undefined) store[attr] = el.getAttribute(attr);
      const orig = store[attr];
      const { lead, core, trail } = splitWS(orig || "");
      const translated = dict[core];
      const next = translated ? lead + translated + trail : orig;
      if (el.getAttribute(attr) !== next) el.setAttribute(attr, next);
    });
  }, []);

  const walk = useCallback(
    (root, dict) => {
      // Text nodes
      const tw = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
      const texts = [];
      let n;
      while ((n = tw.nextNode())) texts.push(n);
      texts.forEach((t) => translateTextNode(t, dict));
      // Attributes on the root + descendants
      if (root.nodeType === 1) translateAttrs(root, dict);
      if (root.querySelectorAll) {
        root.querySelectorAll("[placeholder],[title],[aria-label],[alt]").forEach((el) =>
          translateAttrs(el, dict)
        );
      }
    },
    [translateTextNode, translateAttrs]
  );

  const restoreEnglish = useCallback(() => {
    const tw = document.createTreeWalker(document.body, NodeFilter.SHOW_TEXT);
    let n;
    while ((n = tw.nextNode())) {
      const orig = textOrig.current.get(n);
      if (orig !== undefined && n.nodeValue !== orig) n.nodeValue = orig;
    }
    document.querySelectorAll("[placeholder],[title],[aria-label],[alt]").forEach((el) => {
      const store = attrOrig.current.get(el);
      if (!store) return;
      Object.entries(store).forEach(([attr, val]) => {
        if (val !== undefined && el.getAttribute(attr) !== val) el.setAttribute(attr, val);
      });
    });
  }, []);

  const startObserver = useCallback(() => {
    if (observerRef.current) return;
    const obs = new MutationObserver((muts) => {
      const dict = dictRef.current;
      if (langRef.current === DEFAULT_LANG) return;
      for (const m of muts) {
        if (m.type === "childList") {
          m.addedNodes.forEach((node) => {
            if (node.nodeType === 3) translateTextNode(node, dict);
            else if (node.nodeType === 1) walk(node, dict);
          });
        } else if (m.type === "characterData") {
          // React re-rendered text back to English — re-translate.
          textOrig.current.delete(m.target);
          translateTextNode(m.target, dict);
        } else if (m.type === "attributes" && m.target.nodeType === 1) {
          if (ATTRS.includes(m.attributeName)) {
            const store = attrOrig.current.get(m.target);
            if (store) delete store[m.attributeName];
            translateAttrs(m.target, dict);
          }
        }
      }
    });
    obs.observe(document.body, {
      childList: true,
      subtree: true,
      characterData: true,
      attributes: true,
      attributeFilter: ATTRS,
    });
    observerRef.current = obs;
  }, [translateTextNode, translateAttrs, walk]);

  const stopObserver = useCallback(() => {
    if (observerRef.current) {
      observerRef.current.disconnect();
      observerRef.current = null;
    }
  }, []);

  const applyLang = useCallback(
    async (code) => {
      const meta = langMeta(code);
      document.documentElement.lang = code;
      document.documentElement.dir = meta.dir;
      langRef.current = code;

      if (code === DEFAULT_LANG) {
        stopObserver();
        restoreEnglish();
        dictRef.current = {};
        return;
      }
      const mod = await loaders[code]();
      const dict = mod.default || mod;
      dictRef.current = dict;
      setDictTick((n) => n + 1); // let t() consumers re-render with the loaded dictionary
      stopObserver();
      walk(document.body, dict);
      startObserver();
    },
    [walk, startObserver, stopObserver, restoreEnglish]
  );

  // Initial load — an explicit saved choice always wins; otherwise auto-detect
  // the browser language on first visit.
  useEffect(() => {
    // An explicit ?lang= URL override always wins (used by hreflang alternates).
    const param = detectParamLang();
    if (param) {
      if (param !== DEFAULT_LANG) {
        setLangState(param);
        applyLang(param);
      }
      try {
        localStorage.setItem(STORAGE_KEY, param);
      } catch (e) {
        /* ignore */
      }
      return;
    }
    let stored = null;
    try {
      stored = localStorage.getItem(STORAGE_KEY);
    } catch (e) {
      /* ignore */
    }
    if (stored) {
      // Returning visitor with an explicit preference.
      if (stored !== DEFAULT_LANG) {
        setLangState(stored);
        applyLang(stored);
      }
      return;
    }
    // First visit — no saved choice. Try the browser's language.
    const detected = detectBrowserLang();
    if (detected && detected !== DEFAULT_LANG) {
      setLangState(detected);
      applyLang(detected);
    }
  }, []);

  const setLang = useCallback(
    (code) => {
      setLangState(code);
      try {
        localStorage.setItem(STORAGE_KEY, code);
      } catch (e) {
        /* ignore */
      }
      applyLang(code);
    },
    [applyLang]
  );

  // t() translates a source string through the loaded dictionary. Used by pages
  // whose rendered text carries runtime tokens (e.g. {year}/{dates} on the event
  // guides) that the DOM text-node engine cannot match; plain strings still
  // translate via the DOM walker. No-op in English.
  const t = (s) => dictRef.current[s] || s;

  return (
    <LanguageContext.Provider value={{ lang, setLang, t }}>{children}</LanguageContext.Provider>
  );
}
